// Display formatting helpers: relative timestamps (absolute on hover comes
// from putting absoluteTime() in a title attribute) and person labels.
//
// openedBy / assignedTo / reporterId etc. are bare Entra oids; the API returns
// a `people` map (oid → display name, from the qc_users directory) alongside
// cases. personLabel turns an oid into a label using that map — the resolved
// name (including for the signed-in user — we never collapse to "You", which is
// easy to visually miss), "System" for system events, and a short oid handle
// for anyone who has never signed into QC.

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 45) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 9) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString();
}

export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/**
 * The most recent Friday 17:00 local time — the "New this weekend" boundary
 * (cases filed Fri 5pm onward). On a Friday before 5pm this is LAST week's
 * Friday.
 */
export function lastFriday5pm(now: Date = new Date()): Date {
  const candidate = new Date(now);
  candidate.setHours(17, 0, 0, 0);
  // Walk back to Friday (day 5).
  const day = candidate.getDay();
  const back = (day - 5 + 7) % 7;
  candidate.setDate(candidate.getDate() - back);
  if (candidate.getTime() > now.getTime()) {
    candidate.setDate(candidate.getDate() - 7);
  }
  return candidate;
}

// The all-zero oid the API stamps on system-generated activity (auto-archive,
// auto-reopen). Kept in sync with QC_SYSTEM_ACTOR_ID in @itms-qc/shared.
const SYSTEM_ACTOR_ID = '00000000-0000-0000-0000-000000000000';

/**
 * "You" for the signed-in user; the resolved display name from `people` (the
 * qc_users directory map keyed by lowercased oid) when available; "System" for
 * system-generated events; and a short oid handle as the last-resort fallback
 * (a person who has never signed into QC).
 */
export function personLabel(
  oid: string | null | undefined,
  meOid: string | null,
  people?: Record<string, string>,
  // selfName: the signed-in user's own displayName (from /api/me), used as the
  // fallback when they aren't present in the `people` map.
  opts?: { selfName?: string | null },
): string {
  if (!oid) return 'Unassigned';
  const lower = oid.toLowerCase();
  if (lower === SYSTEM_ACTOR_ID) return 'System';
  // tedious returns UUIDs UPPERCASE; JWT oids are lowercase — normalise both
  // sides before comparing (platform-wide gotcha).
  const isMe = !!meOid && lower === meOid.toLowerCase();
  // Always reference the actual person — never collapse the signed-in user to
  // "You" (easy to visually miss). Resolve from the people directory first,
  // then the signed-in user's own name; "You" only if nothing resolves.
  const name = people?.[lower] ?? (isMe ? opts?.selfName ?? undefined : undefined);
  if (name) return name;
  if (isMe) return 'You';
  return `User ${lower.slice(0, 8)}`;
}

/**
 * Like personLabel, but distinguishes AI-driven actions. When `via` is
 * 'claude-code' the action was performed by an AI agent on the person's behalf,
 * so we surface "Claude Code (AI)" — never "You" — and (in full form) who it
 * acted for. Anything the person did themselves (via null) renders normally.
 */
export function actorLabel(
  oid: string | null | undefined,
  meOid: string | null,
  people: Record<string, string> | undefined,
  via: string | null | undefined,
  opts?: { short?: boolean; selfName?: string | null },
): string {
  if (via === 'claude-code') {
    if (opts?.short) return 'Claude Code (AI)';
    const lower = oid?.toLowerCase();
    const name = (lower && people?.[lower]) || opts?.selfName || 'Chauncey White';
    return `Claude Code (AI) · on behalf of ${name}`;
  }
  return personLabel(oid, meOid, people, opts);
}

/** True when an action was AI-driven (Claude Code on someone's behalf). */
export function isAiVia(via: string | null | undefined): boolean {
  return via === 'claude-code';
}
